/**
 * Dashboardschaal per gebruiker.
 *
 * 18-10-2026 (Jordy): nieuwe chatters openden Chats zo "ingezoomd" dat ze
 * alleen de modelkaarten zagen; de Live Feed en de wachtrijen stonden onder
 * de vouw. Zijn screenshot is de maat: dat is deze opmaak op ~72% op een
 * 1920 brede monitor (de zijbalk is daar ~178 px in plaats van 248).
 *
 * We zetten CSS `zoom` op <html>, niet op een losse container. Dat gedraagt
 * zich als browserzoom: toasts, dialogen en alles wat naar <body> portalt
 * schaalt mee, en scrollen en getBoundingClientRect blijven onderling
 * kloppen (gemeten in Chromium 131 en Firefox 132). Eén uitzondering:
 * `vh` krimpt ook mee. Elke viewporthoogte in theme.css en in inline-stijlen
 * deelt daarom door `var(--dz)`; zie tests/test_dashboard_schaal.py.
 *
 * Media queries (1100px enz.) kijken naar het echte venster, niet naar de
 * geschaalde breedte. Onder 1100px blijft de schaal daarom 1.
 */
import { useEffect, useState } from 'react'
import { useUiPref } from './inklappen'

export const SCHAAL_VOORKEUR = 'ui.dashboard_scale'

export const SCHAAL_KEUZES: { value: string; label: string }[] = [
  { value: 'auto', label: 'Auto (recommended)' },
  { value: '100', label: '100%' },
  { value: '90', label: '90%' },
  { value: '80', label: '80%' },
  { value: '70', label: '70%' },
]

/** 1920 / 0,72: de breedte waarop de opmaak eruitziet als Jordy's screenshot. */
const ONTWERPBREEDTE = 2667
/** Kleiner dan dit wordt de tekst onleesbaar (14px basis → ~10px). */
const MIN_AUTO = 0.72
/** Gelijk aan het breekpunt in theme.css waaronder de zijbalk wegschuift. */
const SMAL = 1100

/** Automatische schaal: evenredig met de vensterbreedte, tussen 0,72 en 1. */
export function autoSchaal(breedte: number): number {
  if (!(breedte > SMAL)) return 1
  const z = Math.min(1, Math.max(MIN_AUTO, breedte / ONTWERPBREEDTE))
  return Math.round(z * 100) / 100
}

/** De schaal voor een opgeslagen keuze ('auto' of een percentage). */
export function schaalVoor(keuze: string, breedte: number): number {
  if (breedte <= SMAL) return 1
  if (/^\d+$/.test(keuze)) {
    const z = Number(keuze) / 100
    if (z >= 0.5 && z <= 1) return z
  }
  return autoSchaal(breedte)
}

/** De keuze van deze gebruiker, bewaard in ui_prefs (alle apparaten). */
export function useSchaalKeuze(): [string, (k: string) => void] {
  const [keuze, zet] = useUiPref<string>(SCHAAL_VOORKEUR, 'auto')
  const geldig = SCHAAL_KEUZES.some((k) => k.value === keuze) ? keuze : 'auto'
  return [geldig, zet]
}

/**
 * Zet de schaal op <html> zolang het dashboard (Layout) open is.
 *
 * `uit`: deze pagina altijd op 1. Voor F2F inloggen: noVNC rekent een klik om
 * met getBoundingClientRect gedeeld door clientWidth, en onder zoom wijken die
 * af (Firefox ~1,6% zelfs met tegen-zoom). Daar moet een klik precies landen.
 */
export function useToepasSchaal(uit = false): number {
  const [keuze] = useSchaalKeuze()
  // window.innerWidth verandert niet door CSS-zoom: geen terugkoppeling.
  const [breedte, setBreedte] = useState(() => window.innerWidth)
  useEffect(() => {
    const f = () => setBreedte(window.innerWidth)
    window.addEventListener('resize', f)
    return () => window.removeEventListener('resize', f)
  }, [])
  const z = uit ? 1 : schaalVoor(keuze, breedte)
  useEffect(() => {
    const el = document.documentElement
    el.style.setProperty('--dz', String(z))
    el.style.setProperty('zoom', z === 1 ? '' : String(z))
    return () => {
      el.style.removeProperty('--dz')
      el.style.removeProperty('zoom')
    }
  }, [z])
  return z
}
